<script setup lang="ts">
import winePlaceholder from '@/assets/images/wine_placeholder.png'
import RatingStars from '@/components/RatingStars.vue'
import { Wine } from '@/types'
import { formatDateRu } from '@/utils/dates'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

// Props
interface Props {
    wine: Wine
    showActions?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    showActions: false,
})

// Emits
const emit = defineEmits<{
    click: [wine: Wine]
    favorite: [wine: Wine, isFavorite: boolean]
    share: [wine: Wine]
}>()

const router = useRouter()

const ratingValue = computed(() => {
    return (props.wine.average_rating || props.wine.user_review?.rating || 0) as number
})

const imageSrc = computed(() => {
    const url = props.wine.bottle_image?.url
    if (!url) return winePlaceholder
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:'))
        return url
    return winePlaceholder
})

const formatDate = (dateString: string) => formatDateRu(dateString)

// Methods
const handleClick = () => {
    emit('click', props.wine)
    router.push({ name: 'wine-info', params: { id: props.wine.id } })
}
</script>

<template>
    <article
        class="wine-card"
        :class="{ 'wine-card_no-rating': ratingValue === 0 }"
        v-if="wine"
        @click="handleClick"
    >
        <div class="wine-card__content">
            <div class="wine-card__rating">
                <RatingStars :rating="ratingValue" size="small" />
            </div>
            <div class="wine-card__wrap">
                <div v-if="wine.created_at" class="wine-card__date">
                    {{ formatDate(wine.created_at) }}
                </div>
                <h3 class="wine-card__title">{{ wine.name }}, {{ wine.year }}</h3>
            </div>
            <p class="wine-card__desc">
                {{
                    wine.description ||
                    'Опишите ваши вкусовые ощущения, сладость, терпкость или чем хотите поделиться'
                }}
            </p>
            <div class="wine-card__cta" @click.stop="handleClick">Подробнее →</div>
        </div>

        <div class="wine-card__image">
            <img :src="imageSrc" :alt="wine.name" />
        </div>
    </article>
</template>

<style scoped lang="stylus">
.wine-card
    display flex
    align-items stretch
    justify-content space-between
    gap 8px
    padding 0 0 24px
    width 100%
    height 244px
    border-bottom 1px solid var(--app-border-light)
    &__content
        display flex
        flex-direction column
        gap 12px

    &__image
        position relative
        min-width 81px
        height 100%
        flex 0 0 81px
        overflow hidden
        img
            width 100%
            height 100%
            object-fit contain
    &__more
        position absolute
        right 4px
        bottom 4px
        display inline-flex
        align-items center
        justify-content center
        width 28px
        height 28px
        border none
        border-radius 999px
        background rgba(0,0,0,.5)
        color var(--app-text-light-color)
        cursor pointer
    // &__rating

    &__date
        font-size 10px
        line-height 12px
        font-weight 400
        color var(--app-hint-color)

    &__title
        font-size 16px
        line-height 21px
        font-weight 600
        color var(--app-text-color)
        text-transform uppercase

    &__desc
        font-size 14px
        line-height 21px
        font-weight 400
        color var(--app-text-color)
        overflow hidden
        display -webkit-box
        -webkit-line-clamp 4
        -webkit-box-orient vertical
        word-break break-word
        ^[0]_no-rating &
            color var(--app-text-disabled)

    &__cta
        margin-top auto
        font-size 16px
        line-height 19px
        font-weight 500
        color var(--app-main-accent-color)
</style>
